import { FormControlLabel, Stack, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import ImageLayer from 'ol/layer/Image';
import ImageStatic from 'ol/source/ImageStatic';
import alertify from 'alertifyjs';
import Polygon from 'ol/geom/Polygon';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import Feature from 'ol/Feature';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import { LineString } from 'ol/geom';
import WKT from 'ol/format/WKT';
import ol from 'openlayers';

import { AmplitudeDomain } from 'features/seismic/models/enums/AmplitudeDomain';
import { useMapStore } from 'features/seismic/stores/useMapStore';
import { useSeismicStore } from 'features/seismic/stores/useSeismicStore';
import { useGetGenericCutsForSeismic, useGetLithology } from './api/useSeismicWellController';
import { useLine3DNavigationStore } from 'features/seismic/stores/useLine3DNavigationStore';
import { getYValue } from 'features/seismic/utils/wellsUtils';
import { LineType } from 'features/seismic/models/enums/LineType';
import { ILineString } from 'features/seismic/models/interfaces/ILineString';
import { SurveyType } from 'features/seismic/models/enums/SurveyType';
import { GeopostCheckbox } from 'components/geopostCheckbox/GeopostCheckbox';
import { useSessionStore } from 'session/useSessionStore';
import { calculateXPosition } from 'features/seismic/utils/calculateScreenValuesUtils';
import { IWellGenericCuts } from 'features/seismic/models/interfaces/IWellGenericCuts';

export function GenericCutsCheckbox({
    wellId,
    trace,
    item,
    domain,
    datum,
    dataGeometryLine,
    genericCutsToRecreate,
    checked,
    surveyType,
    refreshWell,
    hasDirectionalPath,
    getDirectionalPath,
    isErrorDirectionalPath,
    hasTimeDepth,
    setGetDirectionalPath = () => {},
    onChange = () => {},
    isGenericCutLoading = false,
    error = false,
}: Props) {
    const { calculator, surveyMetadata, scale, wellsLayers, setWellLayers, genericCutsWithoutPermission, setGenericCutsWithoutPermission, isInverted } = useSeismicStore(state => ({
        calculator: state.calculator,
        scale: state.scale,
        surveyMetadata: state.surveyMetadata,
        wellsLayers: state.wellsLayers,
        setWellLayers: state.setWellsLayers,
        genericCutsWithoutPermission: state.genericCutsWithoutPermission,
        setGenericCutsWithoutPermission: state.setGenericCutsWithoutPermission,
        isInverted: state.isInverted
    }));

    const { map } = useMapStore(state => ({
        map: state.map
    }));

    const { lineType } = useLine3DNavigationStore(state => ({
        lineType: state.lineType
    }));

    const data = item.split('|');

    const [checkbox, setCheckbox] = useState<boolean>(false);
    const [generatingGenericCut, setGeneratingGenericCut] = useState<boolean>(false);
    const [loadingImage, setLoadingImage] = useState<boolean>(false);
    const lineString = useRef<string>('');
    const [minX, setMinX] = useState<number>(0);
    const [maxX, setMaxX] = useState<number>(0);
    const disabled = useRef<boolean>(false);
    const layer = useRef<ImageLayer<ImageStatic>>();
    const currentLayerExtent = useRef<[number,number,number,number]>();
    // const createGenericCutsImagem = useRef<boolean>(true);

    const s3ImageEndpoint = useSessionStore(state => state.tenantConfiguration?.endpoints.s3Image);

    const { data: dataGenericCuts, isLoading: isLoadingAttributes } = useGetGenericCutsForSeismic(checkbox, wellId, data[1], trace, domain, datum, surveyType);
    const { mutate: fetchLithology, data: dataLithologyImage, isLoading: isLoadingImage } = useGetLithology();

    useEffect(() => {
        if (checkbox &&
            surveyType === SurveyType.Seismic2D &&
            dataGenericCuts?.Token &&
            lineString.current) {
            fetchLithology({
                token: dataGenericCuts.Token,
                wellId: wellId,
                wktString: lineString.current
            });
        }
    }, [lineString.current]);

    useEffect(() => {
        if (getDirectionalPath && wellsLayers[(wellId).toString()] && generatingGenericCut) {
            loadingGenericCuts(true);
            setGetDirectionalPath(false);
        }
    }, [wellsLayers]);

    const genericType = () => {
        let type = data[2];

        if (data[2] === '1') {
            type = 'Lithology';
        }
        else if (data[2] === '2') {
            type = 'Log Curve';
        }
        else if (data[2] === '3') {
            type = 'Other';
        }

        return type;
    };

    const activateGenericType = (checked: boolean, addOrRemove: boolean) => {
        setCheckbox(checked);
        if (surveyType === SurveyType.Seismic2D) {
            if (addOrRemove) {
                showLayer(checked);
            }
        } else {
            onChange(checked, item);
        }
    };

    const loadingGenericCuts = (checked: boolean) => {
        if (checked && surveyMetadata && dataGenericCuts) {
            // if (createGenericCutsImagem.current) {
            /*if (dataGenericCuts.YPosition[0] > 0 || dataGenericCuts.YPosition[1] > 0) {
                dataGenericCuts.YPosition[0] *= -1;
                dataGenericCuts.YPosition[1] *= -1;
            }*/

            const increment = lineType === LineType.Inline ? surveyMetadata.Survey3DInfo.XlineIncrement : surveyMetadata.Survey3DInfo.InlineIncrement;
            //const genericCutsX = getXByTrace(dataGeometryLine.Min + dataGenericCuts.XPosition * increment, scale.x, start, increment, isInverted, calculator!.getTotalTraces(surveyMetadata));
            const surveyStart = calculator!.getMinTrace(surveyMetadata!);
            const surveyEnd = calculator!.getMaxTrace(surveyMetadata!);
            const totalTracesSurvey = calculator!.getTotalTraces(surveyMetadata!);
            /*****/
            let realTrace =  dataGenericCuts.XPosition;
            if (increment > 1) {
                realTrace =  ((dataGenericCuts.XPosition)) - ((dataGeometryLine.Min - surveyStart) / increment);
            }
            const genericCutsX = calculateXPosition(realTrace, totalTracesSurvey, surveyStart, surveyEnd, increment, dataGeometryLine.Min, isInverted, scale.x);
            /*****/

            const genericCutsY = [getYValue(dataGenericCuts.YPosition[0], scale.y, calculator!.calculateSampleInterval(surveyMetadata)), getYValue(dataGenericCuts.YPosition[1], scale.y, calculator!.calculateSampleInterval(surveyMetadata))];

            let needToAddLayer = false;
            if (layer.current) {
                if (currentLayerExtent.current) {
                    if ((currentLayerExtent.current[0] !== genericCutsX) ||
                        (currentLayerExtent.current[1] !== genericCutsY[0]) ||
                        (currentLayerExtent.current[2] !== genericCutsX + 50) ||
                        (currentLayerExtent.current[3] !== genericCutsY[1])) {
                        map?.removeLayer(layer.current);
                        needToAddLayer = true;
                    }
                } else {
                    needToAddLayer = true;
                }
            } else {
                needToAddLayer = true;
            }

            if (needToAddLayer) {
                if (!hasDirectionalPath || isErrorDirectionalPath) {
                    let maxY = Math.max(genericCutsY[0], genericCutsY[1]);
                    let minY = Math.min(genericCutsY[0], genericCutsY[1]);

                    if (domain === AmplitudeDomain.Time) {
                        minY *= -1;
                        maxY *= -1;
                        const aux = minY;
                        minY = Math.min(minY, maxY);
                        maxY = Math.max(aux, maxY);
                    }

                    let imageSource = new ImageStatic({
                        url: `${s3ImageEndpoint}${dataGenericCuts.UrlPartActionTracking}`,
                        imageSize: [dataGenericCuts.WidthImage, dataGenericCuts.HeightImage],
                        imageExtent: [genericCutsX, minY, genericCutsX + 50, maxY]
                    });

                    let imageLayer = new ImageLayer({
                        source: imageSource
                    });

                    currentLayerExtent.current = [genericCutsX, minY, genericCutsX + 50, maxY];
                    map?.addLayer(imageLayer);
                    imageLayer.setVisible(true);
                    layer.current = imageLayer;

                    setLoadingImage(true);

                    imageSource.on('imageloadend', function () {
                        setLoadingImage(false);
                        setGeneratingGenericCut(false);
                        showLayer(true);
                    });
                }
                else if (hasDirectionalPath && !isErrorDirectionalPath && wellsLayers[(wellId).toString()] && wellsLayers[(wellId).toString()]['directionalPath'] && wellsLayers[(wellId).toString()]['directionalPath'].layer) {
                    generateDirectionalLithology(genericCutsY, dataGenericCuts);
                }
                else if (hasDirectionalPath && !isErrorDirectionalPath) {
                    setGetDirectionalPath(true);
                    setGeneratingGenericCut(true);
                    setLoadingImage(true);
                }
            }

            // createGenericCutsImagem.current = false;
        }
    };

    const generateDirectionalLithology = async (genericCutsY: number[], dataGenericCuts: IWellGenericCuts) => {
        let wktString = '';

        const directionalPathLayer = wellsLayers[(wellId).toString()]['directionalPath'].layer;
        if (directionalPathLayer !== null) {
            const source = directionalPathLayer.getSource();
            if (source instanceof VectorSource) {
                const features = source.getFeatures();
                const allCoordinates = features[0].getGeometry() as LineString;

                let coordinates = allCoordinates.getCoordinates();

                let start = coordinates.filter(([x, y]) => y >= genericCutsY[1]);
                let end = coordinates.filter(([x, y]) => y <= genericCutsY[0]);

                if (start.length === 0) {
                    start.push([coordinates[0][0], genericCutsY[1]]);
                }

                if (end.length === 0) {
                    end.push([coordinates[coordinates.length - 1][0], genericCutsY[0]]);
                }

                let filteredCoordinates = coordinates.filter(([x, y]) => y >= genericCutsY[0] && y <= genericCutsY[1]);

                if (filteredCoordinates[0] !== start[start.length - 1]) {
                    filteredCoordinates.unshift(start[start.length - 1]);
                }

                if (filteredCoordinates[filteredCoordinates.length - 1] !== end[0]) {
                    filteredCoordinates.push(end[0]);
                }

                setMinX(Math.min(...filteredCoordinates.map(coord => coord[0])));
                setMaxX(Math.max(...filteredCoordinates.map(coord => coord[0])));

                const line = new LineString(filteredCoordinates);
                const wktFormat = new WKT();
                lineString.current = wktFormat.writeGeometry(line, {
                    dataProjection: 'EPSG:4326',
                });
            }
        }
    };

    useEffect (() => {
        if (dataLithologyImage && !isLoadingImage && dataGenericCuts && surveyMetadata) {
            const generateImage = async () => {
                try {
                    const genericCutsY = [getYValue(dataGenericCuts.YPosition[0], scale.y, calculator!.calculateSampleInterval(surveyMetadata)), getYValue(dataGenericCuts.YPosition[1], scale.y, calculator!.calculateSampleInterval(surveyMetadata))];

                    let maxY = Math.max(genericCutsY[0], genericCutsY[1]);
                    let minY = Math.min(genericCutsY[0], genericCutsY[1]);

                    if (minY >= 0 && maxY >= 0) {
                        minY *= -1;
                        maxY *= -1;
                        const aux = minY;
                        minY = Math.min(minY, maxY);
                        maxY = Math.max(aux, maxY);
                    }

                    const objectUrl = URL.createObjectURL(dataLithologyImage);

                    let heightImage = dataGenericCuts.HeightImage;
                    let widthImage = dataGenericCuts.WidthImage;

                    while (heightImage > 15000)
                    {
                        heightImage /= 2;
                        widthImage /= 2;
                    }

                    let imageSource = new ImageStatic({
                        url: objectUrl,
                        imageSize: [maxX - minX + widthImage, heightImage],
                        imageExtent: [minX, minY, maxX + widthImage, maxY]
                    });
                    let imageLayer = new ImageLayer({
                        source: imageSource
                    });

                    currentLayerExtent.current = [minX, minY, maxX + widthImage, maxY];
                    map?.addLayer(imageLayer);
                    imageLayer.setZIndex(99999);
                    imageLayer.setVisible(true);
                    layer.current = imageLayer;

                    setLoadingImage(false);
                    setGeneratingGenericCut(false);
                    showLayer(true);
                }
                catch (error) {
                    console.error(error);
                    setLoadingImage(false);
                    setGeneratingGenericCut(false);
                }
            };

            generateImage();
        }
    }, [dataLithologyImage]);

    const showLayer = (checked: boolean) => {
        if (layer.current && /* !createGenericCutsImagem.current &&*/ dataGenericCuts) {
            if (checked) {
                layer.current.setVisible(true);
                setWellLayers((wellId).toString(), { [`${genericType()}_${data[1]}`]: { layer: layer.current, checked: true } });
            }
            else {
                layer.current.setVisible(false);
                setWellLayers((wellId).toString(), { [`${genericType()}_${data[1]}`]: { layer: layer.current, checked: false } });
            }
        }
    };

    const errorAlert = () => {
        if (dataGenericCuts?.errorType === 'permission') {
            alertify.alert(`Este tracking pertence ao usuário ${dataGenericCuts?.errorMessage} e não está compartilhado com você.`);
        }
        else {
            alertify.alert(`Ocorreu um erro inesperado`);
        }

        setCheckbox(false);
        //disabled.current = true;

        let notPermited = genericCutsWithoutPermission[wellId.toString()] === undefined ? [] : genericCutsWithoutPermission[wellId.toString()];
        notPermited.push(data[1]);
        setGenericCutsWithoutPermission({ [wellId.toString()]: notPermited });
    };

    useEffect(() => {
        if (dataGenericCuts !== null && dataGenericCuts !== undefined) {
            if (dataGenericCuts.errorType !== null && dataGenericCuts.errorType !== undefined) {
                if (!genericCutsWithoutPermission[wellId.toString()]) {
                    errorAlert();
                }
                else if (!genericCutsWithoutPermission[wellId.toString()].includes(data[1])) {
                    errorAlert();
                }
            }
            else {
                loadingGenericCuts(checkbox);
                showLayer(checkbox);
            }
        }
        else if (dataGenericCuts === null) {
            setCheckbox(false);
            disabled.current = true;
        }
    }, [dataGenericCuts]);

    useEffect(() => {
        if (genericCutsToRecreate.includes((data[1]).toString())) {

            if (!hasDirectionalPath || isErrorDirectionalPath) {
                setCheckbox(false);
                if (layer.current) {
                    map?.removeLayer(layer.current);
                    layer.current = undefined;
                }
            }

            setTimeout(() => {
                setCheckbox(true);
                loadingGenericCuts(true);
                activateGenericType(true, !!dataGenericCuts);
            }, 150);
        }

        if (genericCutsWithoutPermission[wellId.toString()]?.includes(data[1])) {
            setCheckbox(false);
            disabled.current = true;
        }
    }, [refreshWell, genericCutsToRecreate]);

    useEffect(() => {
        console.log(isGenericCutLoading);
    }, [isGenericCutLoading]);

    useEffect(() => {
        if (error) {
            console.log('caiu ERROR -=------------>', error);
        }
    } , [error]);

    useEffect(() => {
        if (isErrorDirectionalPath) {
            setGetDirectionalPath(false);
            setGeneratingGenericCut(false);
            setLoadingImage(false);
            activateGenericType(false, false);
            //disabled.current = true;
        }
    }, [isErrorDirectionalPath]);

    return (
        <Stack direction='row'>
            <FormControlLabel
                control={
                    <GeopostCheckbox
                        id={'wells-' + genericType().toLowerCase() + '-checkbox'}
                        loading={isGenericCutLoading || isLoadingAttributes || generatingGenericCut || loadingImage || isLoadingImage}
                        disabled={disabled.current || !hasTimeDepth}
                        checked={checked ?? checkbox}
                        onChange={checked => {
                            activateGenericType(checked, dataGenericCuts ? true : false);
                        }}
                        error={error || !hasTimeDepth}
                        errorMessage={!hasTimeDepth ? 'Time Depth relation unavailable' : null }
                    />
                }
                label={<Typography>{genericType()} - {data[0]}</Typography>}
            />
        </Stack>
    );
};

interface Props {
    wellId: number,
    trace: number,
    item: string;
    domain: AmplitudeDomain;
    datum: number;
    dataGeometryLine: ILineString;
    genericCutsToRecreate: string[];
    surveyType: SurveyType;
    refreshWell: number;
    hasDirectionalPath: boolean;
    getDirectionalPath: boolean;
    setGetDirectionalPath: (value: boolean) => void;
    isErrorDirectionalPath: boolean;
    onChange?: (checked: boolean, elementToken: string) => void;
    checked?: boolean;
    isGenericCutLoading?: boolean;
    error?: boolean;
    hasTimeDepth: boolean;
}

interface ResponseData {
    Error: boolean;
    Result: { errorMessage: String; errorType: String; }
    Status: number;
}